/* eslint-disable */
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { wget } from 'lodash';
import { connect } from 'react-redux';
import { BarLoader, Button, Filter, Pager } from '@unite-us/app-components';
import {
  useInvalidateQueries,
} from 'src/api/APIHooks';
import { browserHistory } from 'common/utils/browserHistory';
import { getContactFromState } from 'src/components/Contacts/utils';
import { useEligibilityAssessments } from 'src/pages/facesheet/_id/eligibility/hooks/useEligibilityAssessments';
import {
  USE_MY_SCREENINGS_KEY,
} from 'src/pages/facesheet/_id/eligibility/hooks/eligibilityApi';
import EligibilityAssessmentsTable from 'src/pages/facesheet/_id/eligibility/components/EligibilityAssessmentsTable';
import {
  useAssessmentFiltering,
  STATUS_FILTER_OPTIONS,
} from 'src/pages/facesheet/_id/eligibility/hooks/useAssessmentFiltering';

const FILTER_STYLES = {
  filter: {
    maxWidth: 'none',
    minWidth: 0,
    width: '100%',
  },
};

const FILTER_CONFIGS = [
  {
    filterKey: 'name',
    name: 'Form',
    optionsKey: 'nameFilterOptions',
    pluralName: 'Forms',
    uniqIdPrefix: 'name',
  },
  {
    filterKey: 'submitter',
    name: 'Submitter',
    optionsKey: 'submitterFilterOptions',
    pluralName: 'Submitters',
    uniqIdPrefix: 'submitter',
  },
  {
    filterKey: 'status',
    name: 'Status',
    optionsKey: 'statusFilterOptions',
    pluralName: 'Statuses',
    uniqIdPrefix: 'status',
  },
  {
    filterKey: 'organization',
    name: 'Organization',
    optionsKey: 'orgFilterOptions',
    pluralName: 'Organizations',
    uniqIdPrefix: 'organization',
  },
  {
    filterKey: 'date',
    name: 'Last Updated',
    optionsKey: 'dateFilterOptions',
    pluralName: 'Dates',
    uniqIdPrefix: 'date',
  },
];

const emptyMessageFor = (hasFilters, contactName) => (
  hasFilters ?
    'No eligibility assessments match your search criteria. Please try adjusting the filters and search again.' :
    `There are no eligibility assessments for ${contactName}`
);

const FilterControl = ({ config, filterOptions, onFiltersChange }) => (
  <div className="min-w-250px max-w-350px flex-1">
    <Filter
      className="w-full"
      key={config.filterKey}
      name={config.name}
      pluralName={config.pluralName}
      options={filterOptions[config.optionsKey]}
      onFiltersChange={(activeFilters) => onFiltersChange(config.filterKey, activeFilters)}
      filterKey={config.filterKey}
      styles={FILTER_STYLES}
      uniqIdPrefix={config.uniqIdPrefix}
    />
  </div>
);

const AssessmentFilters = ({ filterOptions, onFiltersChange }) => (
  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
    {FILTER_CONFIGS.map((config) => (
      <FilterControl
        key={config.filterKey}
        config={config}
        filterOptions={filterOptions}
        onFiltersChange={onFiltersChange}
      />
    ))}
  </div>
);

const AssessmentActions = ({
  contactId,
  isFetching,
  onNextClick,
  onPrevClick,
  paging,
}) => (
  <div className="ml-auto flex shrink-0 items-center gap-3">
    <Pager
      className="whitespace-nowrap"
      paging={paging}
      disabled={isFetching}
      onNextClick={onNextClick}
      onPrevClick={onPrevClick}
    />
    <Button
      id="add-new-assessment"
      label="Add New Assessment"
      icon={{ name: 'Add', position: 'left', color: 'white' }}
      primary
      onClick={() => browserHistory.push(`/facesheet/${contactId}/eligibility/new`)}
    />
  </div>
);

const AssessmentsToolbar = ({
  contactId,
  filterOptions,
  isFetching,
  onFiltersChange,
  onNextClick,
  onPrevClick,
  paging,
  totalCount,
}) => (
  <>
    <div className="mb-4">
      <h2 className="form-name my-2">
        {`${totalCount} Eligibility Assessment${totalCount !== 1 ? 's' : ''}`}
      </h2>
    </div>
    <div className="mb-6 flex flex-wrap items-start gap-3">
      <AssessmentFilters
        filterOptions={filterOptions}
        onFiltersChange={onFiltersChange}
      />
      <AssessmentActions
        contactId={contactId}
        isFetching={isFetching}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
        paging={paging}
      />
    </div>
  </>
);

export function EligibilityAssessments({
  contact,
  children,
}) {
  const invalidateQueries = useInvalidateQueries();
  const { data: assessments, isFetching } = useEligibilityAssessments(contact.id);

  const {
    sortBy,
    sortDirection,
    hasFilters,
    paginatedAssessments,
    totalCount,
    paging,
    orgFilterOptions,
    nameFilterOptions,
    submitterFilterOptions,
    dateFilterOptions,
    onSortChange,
    onFiltersChange,
    onNextClick,
    onPrevClick,
  } = useAssessmentFiltering(assessments);
  const filterOptions = {
    dateFilterOptions,
    nameFilterOptions,
    orgFilterOptions,
    statusFilterOptions: STATUS_FILTER_OPTIONS,
    submitterFilterOptions,
  };

  useEffect(() => {
    invalidateQueries(USE_MY_SCREENINGS_KEY);
  }, []);

  const emptyMessage = emptyMessageFor(hasFilters, contact.full_name);

  if (children) {
    return children;
  }

  return (
    <div className="px-5">
      <AssessmentsToolbar
        contactId={contact.id}
        filterOptions={filterOptions}
        isFetching={isFetching}
        onFiltersChange={onFiltersChange}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
        paging={paging}
        totalCount={totalCount}
      />
      {isFetching ? (
        <BarLoader className="h-60" size="full" />
      ) : (
        <EligibilityAssessmentsTable
          assessments={paginatedAssessments}
          contactId={contact.id}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
          emptyMessage={emptyMessage}
        />
      )}
    </div>
  );
};

EligibilityAssessments.propTypes = {
  contact: PropTypes.object.isRequired,
  children: PropTypes.node,
};

EligibilityAssessments.defaultProps = {
  children: null,
};

function mapStateToProps(state, ownProps) {
  const contactId = wget(ownProps, 'params.id', '');
  const contact = getContactFromState(state, contactId);
  const currentProvider = state.globalState.currentProvider.group;
  return {
    contact,
    provider: currentProvider,
  };
}

export default connect(mapStateToProps)(EligibilityAssessments);
